/**
 * @fileoverview Entry point for the system-web admin portal.
 * @module system-web/main
 *
 * Responsibilities: - Bootstrap React app, fetch web config, initialize file service resolver.
 * Dependencies:     - React, ReactDOM, AppErrorBoundary, fetchWebConfig, fileServiceBootstrap.
 * Architecture:     - Standard Vite entry point; initializes services before rendering.
 * Notes:            - System-web is the platform-wide administrative dashboard.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { fetchWebConfig } from './config';
import { initFileServiceResolver } from './services/fileServiceBootstrap';
import { initTelemetry } from './services/telemetry';
import './index.css';

const container = document.getElementById('root') as HTMLElement | null;
if (!container) {
  throw new Error('Root container #root not found');
}

// Ensure React mounts into a clean container: remove the static boot-screen
// markup defined in index.html so React fully owns the subtree.
container.innerHTML = '';

// Self-healing: unregister any stale service worker from a previous build
// before registering the current one. A previous VitePWA-generated SW can
// survive across deployments and serve cached JS/CSS that doesn't match the
// current index.html, causing the app to hang on a stale bundle. We
// aggressively unregister ALL existing SWs and clear their caches, then
// register the current self-destructing sw.js.
//
// CRITICAL: SW cleanup MUST complete before fetchWebConfig() fires. A stale
// SW still controls the current tab until it's unregistered AND the page is
// reloaded — unregistering alone does not revoke control of the active tab.
// If the config fetch races with SW teardown, the stale SW intercepts
// /api/web-config, never responds (it's in a broken state), and the 3s
// per-request timeout aborts the fetch with "signal is aborted without
// reason". This produced 7+ spurious retry attempts on every fresh-tab load.
// Awaiting cleanup first eliminates the race. Mirrors tenant-web's
// initAndRender() ordering.
async function cleanupStaleServiceWorkers(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (registrations.length > 0) {
      console.log(`[main] Found ${registrations.length} stale service worker(s) — unregistering`);
      await Promise.all(registrations.map((reg) => reg.unregister()));
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }
      console.log('[main] Stale service workers and caches cleared');
    }
  } catch (err) {
    console.warn('[main] SW cleanup failed (non-fatal):', err);
  }
  // Register the minimal service worker so Chrome auto-grants
  // navigator.storage.persist() for this origin. The SW does not cache
  // anything — it exists solely to satisfy Chrome's persistence auto-grant
  // criteria (Chrome requires an active SW for non-installed origins).
  // updateViaCache: 'none' prevents the browser from reusing a stale HTTP
  // cache entry for sw.js after a code change.
  navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch((err) => {
    console.warn('[main] SW registration failed (non-fatal):', (err as Error)?.message);
  });
}

async function initAndRender(): Promise<void> {
  // 1. Tear down stale SWs BEFORE any network fetch so they can't intercept
  //    the config request. See cleanupStaleServiceWorkers docblock for why.
  await cleanupStaleServiceWorkers();

  // 2. Initialize the unified file URL resolver (SAS URL caching + scope fallback).
  initFileServiceResolver();

  // 3. Load runtime config from system-api Key Vault before rendering.
  //    Non-blocking — falls back to VITE_ env vars / Docker runtime config on
  //    failure.
  await fetchWebConfig().catch(() => { /* handled inside fetchWebConfig */ });

  // 4. Boot Application Insights once runtime config is resolved. Non-blocking
  //    and never throws — telemetry is disabled gracefully when no connection
  //    string is set.
  void initTelemetry();

  ReactDOM.createRoot(container!).render(
    <React.StrictMode>
      <AppErrorBoundary>
        <App />
      </AppErrorBoundary>
    </React.StrictMode>,
  );
}

void initAndRender();
